import React, { useCallback, useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import {
  FaBan,
  FaCheckCircle,
  FaPaperPlane,
  FaPlus,
  FaRecycle,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"
import { useAuth } from "../../provider/auth.provider"
import { useConfirm } from "../../provider/confirm.provider"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import {
  apiError,
  dateText,
  fetchPaged,
  FilterOption,
  fullName,
  HolderType,
  materialFilters,
  materialMasters,
  money,
  qty,
} from "./shared/material.api"
import {
  AsyncPicker,
  Cell,
  DataTable,
  DetailRow,
  ExpandAction,
  Field,
  FilterField,
  FilterRow,
  GhostButton,
  HolderChip,
  HolderPicker,
  IconAction,
  ListCard,
  OffCanvas,
  PrimaryButton,
  RefreshButton,
  ScopeNote,
  SearchBox,
  Select,
  StatusBadge,
  StatusTabs,
  Td,
  TextArea,
  TextInput,
  Th,
} from "./shared/MaterialUI"

/**
 * Material Scrap Report (MSR) - every way material leaves the books without
 * being installed.
 *
 *   Discarded   written off at site or in the warehouse.
 *   Sold        sold as-is; the receipt and the amount are recorded.
 *   Lost        lost by a staff member. Recorded against that person and shown
 *               in the staff reco. No money is tracked and no payroll
 *               deduction is raised - recovery is settled outside the system.
 *   Adjustment  a correction, and the only type that can also ADD stock.
 *
 * One approval, and it is the approval that writes the material off.
 */

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "submitted", label: "Awaiting Approval" },
  { key: "approved", label: "Approved" },
  { key: "rejected,cancelled", label: "Closed" },
]

const TYPE_LABEL: Record<string, string> = {
  discarded: "Discarded",
  sold: "Sold",
  lost: "Lost by staff",
  adjustment: "Adjustment",
}

const TYPE_TONE: Record<string, string> = {
  discarded: "bg-orange-100 text-orange-800",
  sold: "bg-emerald-100 text-emerald-800",
  lost: "bg-red-100 text-red-800",
  adjustment: "bg-indigo-100 text-indigo-800",
}

interface LineDraft {
  key: string
  item: FilterOption | null
  quantity: string
  unit_rate: string
  remarks: string
}

const blankLine = (): LineDraft => ({
  key: Math.random().toString(36).slice(2),
  item: null,
  quantity: "",
  unit_rate: "",
  remarks: "",
})

const MaterialScrapsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { confirm, prompt } = useConfirm()
  const canCreate = hasPermission("MaterialScrap", "create")
  const canApprove = hasPermission("MaterialScrap", "approve")
  const canCancel = hasPermission("MaterialScrap", "cancel")

  const [rows, setRows] = useState<any[]>([])
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [status, setStatus] = useState("")
  const [scrapType, setScrapType] = useState("")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search, 400)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [showForm, setShowForm] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchPaged("/materials/scraps", {
        page,
        limit,
        status: status || undefined,
        scrap_type: scrapType || undefined,
        project_id: project?.id,
        search: debouncedSearch.trim() || undefined,
      })
      setRows(result.rows)
      setPagination(result.pagination)
    } catch (e: any) {
      toast.error(apiError(e, "Failed to load scrap reports"))
    } finally {
      setLoading(false)
    }
  }, [page, limit, status, scrapType, project, debouncedSearch])

  useEffect(() => {
    load()
  }, [load])

  const act = async (row: any, action: "submit" | "approve" | "reject" | "cancel") => {
    let body: Record<string, any> = {}

    if (action === "reject" || action === "cancel") {
      const entered = await prompt({
        title: action === "reject" ? "Reject scrap report" : "Cancel scrap report",
        message: "The person who raised it will see your reason.",
        label: "Reason",
        required: true,
        multiline: true,
        confirmText: action === "reject" ? "Reject" : "Cancel report",
        cancelText: "Go back",
        variant: "warning",
      })
      if (entered === null) return
      body = { remarks: entered, reason: entered }
    }

    if (action === "approve") {
      const ok = await confirm({
        title: `Approve ${TYPE_LABEL[row.scrap_type] || "scrap"} report`,
        message: (
          <>
            This writes the listed material {row.direction === "increase" ? "on to" : "off"}{" "}
            <strong>{row.holder_label || "the holder"}</strong> for good.
          </>
        ),
        warning:
          row.scrap_type === "lost"
            ? "A loss is recorded against the staff member and shows in their reconciliation. No deduction is raised here."
            : undefined,
        confirmText: "Approve",
        cancelText: "Not yet",
        variant: "warning",
      })
      if (!ok) return
    }

    setBusyId(row.id)
    try {
      const res = await api.post(`/materials/scraps/${row.id}/${action}`, body)
      toast.success(res.data?.message || "Done")
      load()
    } catch (e: any) {
      toast.error(apiError(e, "The action could not be completed"))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-4">
      <ListCard
        icon={<FaRecycle className="w-5 h-5" />}
        title="Scrap, Sale, Loss & Adjustment (MSR)"
        subtitle="Material that leaves the books without being installed. Approving is what writes it off - an adjustment is the only type that can also add stock back."
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            {canCreate ? (
              <PrimaryButton onClick={() => setShowForm(true)}>
                <FaPlus className="w-4 h-4" /> New Report
              </PrimaryButton>
            ) : null}
          </>
        }
        filters={
          <>
            <FilterRow>
              <FilterField label="Search" grow>
                <SearchBox
                  value={search}
                  onChange={(v) => {
                    setSearch(v)
                    setPage(1)
                  }}
                  placeholder="MSR number, buyer, receipt..."
                />
              </FilterField>
              <FilterField label="Project">
                <AsyncPicker
                  value={project}
                  onChange={(o) => {
                    setProject(o)
                    setPage(1)
                  }}
                  fetcher={(s) => materialFilters.projects(s)}
                  placeholder="All my projects"
                  emptyHint="You are not assigned to any project"
                />
              </FilterField>
              <FilterField label="Type">
                <Select
                  value={scrapType}
                  onChange={(e) => {
                    setScrapType(e.target.value)
                    setPage(1)
                  }}
                >
                  <option value="">All types</option>
                  <option value="discarded">Discarded</option>
                  <option value="sold">Sold</option>
                  <option value="lost">Lost by staff</option>
                  <option value="adjustment">Adjustment</option>
                </Select>
              </FilterField>
            </FilterRow>
            <StatusTabs
              tabs={STATUS_TABS}
              value={status}
              onChange={(k) => {
                setStatus(k)
                setPage(1)
              }}
            />
          </>
        }
        footer={
          pagination ? (
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l)
                setPage(1)
              }}
            />
          ) : null
        }
      >
        <DataTable
          colSpan={8}
          loading={loading}
          empty={rows.length === 0}
          loadingText="Loading scrap reports..."
          emptyText="No scrap reports found"
          emptyHint={<ScopeNote />}
          emptyIcon={<FaRecycle />}
          head={
            <>
              <Th>Actions</Th>
              <Th>Report</Th>
              <Th>Type</Th>
              <Th>Holder</Th>
              <Th>Date</Th>
              <Th>Lines</Th>
              <Th>Sale Amount</Th>
              <Th>Status</Th>
            </>
          }
        >
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="hover:bg-gray-50 transition-colors">
                <Cell>
                  <div className="flex items-center gap-1">
                    <ExpandAction
                      open={expanded === row.id}
                      onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                      what="items"
                    />
                    {row.status === "draft" && canCreate ? (
                      <IconAction title="Submit for approval" disabled={busyId === row.id} onClick={() => act(row, "submit")}>
                        <FaPaperPlane />
                      </IconAction>
                    ) : null}
                    {row.status === "submitted" && canApprove ? (
                      <>
                        <IconAction title="Approve" tone="green" disabled={busyId === row.id} onClick={() => act(row, "approve")}>
                          <FaCheckCircle />
                        </IconAction>
                        <IconAction title="Reject" tone="red" disabled={busyId === row.id} onClick={() => act(row, "reject")}>
                          <FaTimesCircle />
                        </IconAction>
                      </>
                    ) : null}
                    {!["approved", "cancelled"].includes(row.status) && canCancel ? (
                      <IconAction title="Cancel report" tone="gray" disabled={busyId === row.id} onClick={() => act(row, "cancel")}>
                        <FaBan />
                      </IconAction>
                    ) : null}
                  </div>
                </Cell>
                <Cell>
                  <div className="font-medium">{row.scrap_number}</div>
                  <div className="text-xs text-gray-500">
                    {row.project?.project_short_name || row.project?.project_name || "-"}
                  </div>
                </Cell>
                <Cell>
                  <span className={`inline-flex px-2 py-0.5 text-xs rounded ${TYPE_TONE[row.scrap_type] || "bg-gray-100"}`}>
                    {TYPE_LABEL[row.scrap_type] || row.scrap_type}
                  </span>
                  {row.direction === "increase" ? <div className="text-[11px] text-green-700 mt-0.5">adds stock</div> : null}
                </Cell>
                <Cell>
                  <HolderChip type={row.holder_type} label={row.holder_label} code={row.holder_code} />
                </Cell>
                <Cell>{dateText(row.scrap_date)}</Cell>
                <Cell>{(row.items || []).length}</Cell>
                <Cell>{row.sale_amount ? money(row.sale_amount) : "-"}</Cell>
                <Cell>
                  <StatusBadge status={row.status} />
                </Cell>
              </tr>
              {expanded === row.id ? (
                <DetailRow colSpan={8}>
                  <ScrapDetail row={row} />
                </DetailRow>
              ) : null}
            </React.Fragment>
          ))}
        </DataTable>
      </ListCard>

      {showForm ? (
        <ScrapForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false)
            setPage(1)
            load()
          }}
        />
      ) : null}
    </div>
  )
}

const ScrapDetail: React.FC<{ row: any }> = ({ row }) => (
  <div className="space-y-3 py-2">
    <table className="min-w-full text-sm bg-white border border-gray-200 rounded">
      <thead className="bg-gray-100">
        <tr>
          <Th>Item</Th>
          <Th>Quantity</Th>
          <Th>Rate</Th>
          <Th>Value</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {(row.items || []).map((line: any) => (
          <tr key={line.id}>
            <Td>
              {line.item?.item_name || `#${line.item_id}`}
              {line.item?.item_code ? <span className="text-xs text-gray-400"> ({line.item.item_code})</span> : null}
            </Td>
            <Td>{qty(line.quantity)}</Td>
            <Td>{line.unit_rate ? money(line.unit_rate) : "-"}</Td>
            <Td>{line.unit_rate ? money(Number(line.quantity) * Number(line.unit_rate)) : "-"}</Td>
            <Td className="text-gray-500">{line.remarks || "-"}</Td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-600">
      <div>
        <span className="font-medium text-gray-800">Reason:</span> {row.reason || "-"}
      </div>
      <div>
        <span className="font-medium text-gray-800">Reported by:</span> {fullName(row.reporter)}
      </div>
      {row.scrap_type === "sold" ? (
        <>
          <div>
            <span className="font-medium text-gray-800">Sold to:</span> {row.sold_to || "-"}
          </div>
          <div>
            <span className="font-medium text-gray-800">Receipt:</span> {row.receipt_number || "-"}
          </div>
        </>
      ) : null}
      {row.scrap_type === "lost" ? (
        <div className="md:col-span-2 text-amber-800">
          Recorded as a loss against this staff member. It shows as a reducing line in their reconciliation; any
          recovery is settled outside the system.
        </div>
      ) : null}
      {row.rejection_remarks ? (
        <div className="md:col-span-2 text-red-700">
          <span className="font-medium">Rejected:</span> {row.rejection_remarks}
        </div>
      ) : null}
    </div>
  </div>
)

const ScrapForm: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [saving, setSaving] = useState(false)
  const [scrapType, setScrapType] = useState<"discarded" | "sold" | "lost" | "adjustment">("discarded")
  const [direction, setDirection] = useState<"decrease" | "increase">("decrease")
  const [project, setProject] = useState<FilterOption | null>(null)
  const [holderType, setHolderType] = useState<HolderType>("site")
  const [holder, setHolder] = useState<FilterOption | null>(null)
  const [scrapDate, setScrapDate] = useState(new Date().toISOString().slice(0, 10))
  const [form, setForm] = useState<Record<string, string>>({
    reason: "",
    sale_amount: "",
    sold_to: "",
    receipt_number: "",
    remarks: "",
  })
  const [lines, setLines] = useState<LineDraft[]>([blankLine()])

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }))

  // A loss belongs to a person, so the holder is forced to staff. Everything
  // else can come off any holder.
  useEffect(() => {
    if (scrapType === "lost") {
      setHolderType("staff")
      setHolder(null)
    }
    if (scrapType !== "adjustment") setDirection("decrease")
  }, [scrapType])

  const fetchers = useMemo(
    () => ({
      warehouse: (s: string) => materialMasters.warehouses(s),
      staff: (s: string) => materialFilters.staff(project ? String(project.id) : undefined, s),
      site: (s: string) => materialFilters.sites(project ? String(project.id) : undefined, s),
    }),
    [project],
  )

  const submit = async (submitForApproval: boolean) => {
    if (!project) return toast.error("Pick a project")
    if (!holder) return toast.error("Pick whose stock this comes off")
    const validLines = lines.filter((l) => l.item && Number(l.quantity) > 0)
    if (!validLines.length) return toast.error("Add at least one item line with a quantity")

    setSaving(true)
    try {
      const res = await api.post("/materials/scraps", {
        project_id: project.id,
        scrap_type: scrapType,
        direction,
        holder_type: holderType,
        holder_id: holder.id,
        inc_site_id: holderType === "site" ? holder.id : undefined,
        scrap_date: scrapDate,
        submit: submitForApproval,
        ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "")),
        items: validLines.map((l) => ({
          item_id: l.item!.id,
          quantity: Number(l.quantity),
          unit_rate: l.unit_rate ? Number(l.unit_rate) : null,
          remarks: l.remarks || null,
        })),
      })
      toast.success(res.data?.message || "Scrap report created")
      onSaved()
    } catch (e: any) {
      toast.error(apiError(e, "Failed to create the scrap report"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <OffCanvas
      onClose={onClose}
      title="New scrap / sale / loss / adjustment"
      subtitle="Approval is what moves the stock. Nothing changes while this is a draft or waiting."
      width="xl"
      footer={
        <>
          <GhostButton onClick={() => submit(false)} disabled={saving}>
            Save as draft
          </GhostButton>
          <PrimaryButton loading={saving} onClick={() => submit(true)}>
            Submit for approval
          </PrimaryButton>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Type" required>
          <Select value={scrapType} onChange={(e) => setScrapType(e.target.value as any)}>
            <option value="discarded">Discarded</option>
            <option value="sold">Sold</option>
            <option value="lost">Lost by staff</option>
            <option value="adjustment">Adjustment</option>
          </Select>
        </Field>
        <Field label="Project" required>
          <AsyncPicker
            value={project}
            onChange={setProject}
            fetcher={(s) => materialFilters.projects(s)}
            placeholder="Select project..."
            emptyHint="You are not assigned to any project"
          />
        </Field>
        <Field label="Date" required>
          <TextInput type="date" value={scrapDate} onChange={(e) => setScrapDate(e.target.value)} />
        </Field>

        <div className="md:col-span-2">
          <Field
            label="Holder"
            required
            hint={scrapType === "lost" ? "A loss is recorded against the staff member who lost it" : undefined}
          >
            <HolderPicker
              holderType={holderType}
              onHolderTypeChange={setHolderType}
              value={holder}
              onChange={setHolder}
              fetchers={fetchers}
              allowed={scrapType === "lost" ? ["staff"] : ["site", "staff", "warehouse"]}
              dependency={project?.id}
              disabled={!project}
            />
          </Field>
        </div>
        {scrapType === "adjustment" ? (
          <Field label="Direction" hint="An adjustment is the only type that can add stock back">
            <Select value={direction} onChange={(e) => setDirection(e.target.value as any)}>
              <option value="decrease">Decrease stock</option>
              <option value="increase">Increase stock</option>
            </Select>
          </Field>
        ) : null}
      </div>

      {scrapType === "sold" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Sale amount">
            <TextInput
              type="number"
              min="0"
              step="0.01"
              value={form.sale_amount}
              onChange={(e) => set("sale_amount", e.target.value)}
            />
          </Field>
          <Field label="Sold to">
            <TextInput value={form.sold_to} onChange={(e) => set("sold_to", e.target.value)} />
          </Field>
          <Field label="Receipt number">
            <TextInput value={form.receipt_number} onChange={(e) => set("receipt_number", e.target.value)} />
          </Field>
        </div>
      ) : null}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Material</h3>
          <GhostButton onClick={() => setLines((l) => [...l, blankLine()])}>
            <FaPlus /> Add line
          </GhostButton>
        </div>
        <div className="border border-gray-200 rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th className="w-2/5">Item</Th>
                <Th>Quantity</Th>
                <Th>Rate</Th>
                <Th>Remarks</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((line, index) => (
                <tr key={line.key}>
                  <Td>
                    <AsyncPicker
                      value={line.item}
                      onChange={(o) => setLines((ls) => ls.map((l, i) => (i === index ? { ...l, item: o } : l)))}
                      fetcher={(s) => materialMasters.items(s)}
                      placeholder="Select item..."
                    />
                  </Td>
                  <Td>
                    <TextInput
                      type="number"
                      min="0"
                      step="0.001"
                      value={line.quantity}
                      onChange={(e) =>
                        setLines((ls) => ls.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l)))
                      }
                    />
                  </Td>
                  <Td>
                    <TextInput
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unit_rate}
                      onChange={(e) =>
                        setLines((ls) => ls.map((l, i) => (i === index ? { ...l, unit_rate: e.target.value } : l)))
                      }
                    />
                  </Td>
                  <Td>
                    <TextInput
                      value={line.remarks}
                      onChange={(e) =>
                        setLines((ls) => ls.map((l, i) => (i === index ? { ...l, remarks: e.target.value } : l)))
                      }
                    />
                  </Td>
                  <Td>
                    <button
                      onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== index) : ls))}
                      className="text-red-500 hover:text-red-700 disabled:opacity-30"
                      disabled={lines.length === 1}
                    >
                      <FaTrash />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-500 mt-1">
          The rate is optional and only values the write-off in the reconciliation reports.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Reason">
          <TextArea rows={2} value={form.reason} onChange={(e) => set("reason", e.target.value)} />
        </Field>
        <Field label="Remarks">
          <TextArea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
        </Field>
      </div>
    </OffCanvas>
  )
}

export default MaterialScrapsPage
